import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getUserInformation } from "../../store/userRole";
import FaLock from "../../constants/icons/Fa/FaLock";
import { useQuery } from "react-query";
import { fetchSubscriptionByUserId } from "../../store/subscriptionSlice";

// Dummy skeleton loader component
const SkeletonLoader = () => (
  <div
    style={{
      width: "100px",
      height: "24px",
      backgroundColor: "#e0e0e0",
      borderRadius: "4px",
      marginRight: "10px",
    }}
  ></div>
);

const withSubscriptionCheckButton = (
  WrappedButton,
  requiredFeature,
  checkMaxAllowed = false,
  isExamCheck = false,
  className = ""
) => {
  const SubscriptionCheckButtonHOC = (props) => {
    const [hasValidSubscription, setHasValidSubscription] = useState(false);
    const [isTrialActive, setIsTrialActive] = useState(false);
    const [isLocked, setIsLocked] = useState(false); // New state to handle lock
    const [showSkeleton, setShowSkeleton] = useState(true); // Control Skeleton display
    const userInformation = useSelector(getUserInformation);
    const navigate = useNavigate();
    const { classRoomId, subjects, onClick, index } = props;

    // Check if the user is within the first 24 hours for trial
    const isFirst24Hours = isWithinFirst24Hours(userInformation, classRoomId);

    // Use react-query's useQuery for fetching and caching subscription data
    const {
      data: subscriptionData,
      isLoading,
      isError,
    } = useQuery(
      ["subscriptionData", classRoomId, userInformation?._id],
      () => fetchSubscriptionByUserId(classRoomId, userInformation?._id),
      {
        staleTime: 60000, // 10 seconds
        cacheTime: 1000 * 60 * 10, // 10 minutes
        retry: false, // Disable retries to avoid looping on errors
        enabled: !isFirst24Hours, // Disable query if within first 24 hours
      }
    );

    // Evaluate subscription data and check access permissions
    useEffect(() => {
      if (isFirst24Hours && (!subscriptionData || subscriptionData.error)) {
        // If in the first 24 hours, allow access
        setIsTrialActive(true);
        setHasValidSubscription(true);
        setShowSkeleton(false); // Stop showing the skeleton
      } else if (subscriptionData && !isError) {
        evaluateSubscription(subscriptionData);
        setShowSkeleton(false); // Stop showing skeleton after data loads
      } else if (isError || (subscriptionData && subscriptionData.error)) {
        // Handle the case where the subscription is missing or trial expired
        const errorMsg = subscriptionData?.error;

        if (errorMsg && errorMsg.includes("No Subscription found")) {
          // Lock everything if no subscription is found
          setIsLocked(true);
        } else {
          setIsLocked(false);
        }
        setIsTrialActive(false); // Trial has expired
        setShowSkeleton(false); // Stop showing the skeleton
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscriptionData, isError, isFirst24Hours]);

    const evaluateSubscription = (data) => {
      if (!data || data.error) {
        setHasValidSubscription(false); // No valid subscription
        return;
      }

      const { pricingModel, features } = data;
      let isValid = false;

      const feature = features?.find(
        (feature) =>
          feature.name === requiredFeature && feature.isLocked !== true
      );

      if (feature) {
        const maxAllowed = feature.maxAllowed || 0;

        if (!checkMaxAllowed || (checkMaxAllowed && index < maxAllowed)) {
          if (isExamCheck) {
            if(pricingModel === 'Hourly Plan') {
              isValid = checkHourlyPlanValidity(data);
            } else {
              isValid = checkSubscriptionValidity(data);
            }
          } else {
            switch (pricingModel) {
              case "Free":
                isValid = hasSubjectAccess(data, subjects);
                break;
              case "One Time Payment":
              case "Subscription":
              case "Installment Plan":
                isValid =
                  checkSubscriptionValidity(data) &&
                  hasSubjectAccess(data, subjects);
                break;
              case "Hourly Plan":
                isValid =
                  checkHourlyPlanValidity(data) &&
                  hasSubjectAccess(data, subjects);
                break;
              default:
                break;
            }
          }
        }
      }

      setHasValidSubscription(isValid);
    };

    const hasSubjectAccess = (data, subjects) => {
      const { attachedTo = [] } = data;
      return subjects?.some((subjectId) => attachedTo.includes(subjectId));
    };

    const handleButtonClick = () => {
      if (!hasValidSubscription) {
        navigate(`/student/myClassrooms/${classRoomId}/pricing`);
      } else {
        onClick();
      }
    };

    if (showSkeleton && isLoading) {
      return <SkeletonLoader />;
    }

    if (isLocked || (!hasValidSubscription && !isTrialActive)) {
      return (
        <div className="d-flex align-items-center">
          <FaLock style={{ marginRight: "10px", color: "#ccc" }} />
          <WrappedButton
            {...props}
            onClick={handleButtonClick}
            className={className ? className : "start-classroom-styles"}
            variant="outline-primary"
          />
        </div>
      );
    }

    return (
      <div className="d-flex align-items-center">
        <WrappedButton
          {...props}
          onClick={handleButtonClick}
          className={className ? className : "start-classroom-styles"}
          variant="outline-primary"
        />
      </div>
    );
  };

  return SubscriptionCheckButtonHOC;
};

// Helper functions
function isWithinFirst24Hours(userInformation, classRoomId) {
  const currentClassroom = userInformation?.classrooms?.find(
    (classroom) => classroom._id === classRoomId
  );

  if (!currentClassroom) return false; // If no classroom data is found
  const creationDate = new Date(currentClassroom?.createdAt);
  const currentDate = new Date();
  const diffInMilliseconds = currentDate - creationDate;
  const hoursDiff = diffInMilliseconds / (1000 * 60 * 60);
  return hoursDiff <= 24;
}

function checkSubscriptionValidity(subscriptionData) {
  const endDate = new Date(subscriptionData?.endDate);
  endDate.setHours(23, 59, 59, 999);
  const currentDate = new Date();
  return currentDate <= endDate;
}

function checkHourlyPlanValidity(subscriptionData) {
  // Check if student has remaining hours for hourly plans
  const totalHours = parseFloat(subscriptionData?.totalHoursOfStudent) || 0;
  const consumedHours = parseFloat(subscriptionData?.hoursConsumedOfStudent) || 0;
  const remainingHours = totalHours - consumedHours;
  return remainingHours > 0;
}

withSubscriptionCheckButton.propTypes = {
  requiredFeature: PropTypes.string.isRequired,
};

export default withSubscriptionCheckButton;
